// ============================================================
// SETTINGS PAGE
// ============================================================

import React, { useState } from 'react';
import { Settings as SettingsIcon, Save, Bell, Shield, Globe, Database } from 'lucide-react';
import { Button, Toggle, Alert } from '../components/ui';
import { toast } from 'react-toastify';

const defaultSettings = {
  appName: 'Biryani Hub',
  supportEmail: 'support@biryanihub.com',
  contactPhone: '+91 98765 43210',
  minOrderAmount: 149,
  maxDeliveryRadius: 15,
  defaultCommissionRate: 15,
  gstPercent: 5,
  maintenanceMode: false,
  registrationsOpen: true,
  emailNotifications: true,
  smsNotifications: true,
  pushNotifications: true,
  orderAlerts: true,
  paymentAlerts: true,
  twoFactorAdmin: false,
  ipWhitelist: '',
  timezone: 'Asia/Kolkata',
  currency: 'INR',
  language: 'en',
  autoSettlement: true,
  settlementFrequency: 'weekly',
  refundAutoApprove: false,
  refundLimit: 500,
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="admin-card" style={{ marginBottom: 20 }}>
    <div className="admin-card-header">
      <h3 className="admin-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon size={16} />{title}
      </h3>
    </div>
    <div className="admin-card-body">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="admin-form-group">{label && <label>{label}</label>}{children}</div>
);

const ToggleRow = ({ label, desc, value, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--admin-border)', gridColumn: '1 / -1' }}>
    <div><div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>{desc && <div style={{ fontSize: 12, color: '#94A3B8' }}>{desc}</div>}</div>
    <Toggle checked={value} onChange={onChange} />
  </div>
);

const Settings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [saved, setSaved] = useState(false);

  const set = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  const save = () => {
    setSaved(true);
    toast.success('Settings saved successfully!');
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Platform Settings</h1>
          <p className="admin-page-subtitle">Configure global platform parameters</p>
        </div>
        <Button variant="primary" icon={<Save size={14} />} onClick={save}>Save All Changes</Button>
      </div>

      {saved && <Alert type="success" message="All settings saved successfully!" style={{ marginBottom: 20 }} />}

      {settings.maintenanceMode && <Alert type="warning" message="⚠️ Maintenance Mode is ACTIVE — users cannot access the app." style={{ marginBottom: 20 }} />}

      <Section icon={Globe} title="General Settings">
        <Field label="App Name"><input className="admin-form-control" value={settings.appName} onChange={e => set('appName', e.target.value)} /></Field>
        <Field label="Support Email"><input className="admin-form-control" type="email" value={settings.supportEmail} onChange={e => set('supportEmail', e.target.value)} /></Field>
        <Field label="Contact Phone"><input className="admin-form-control" value={settings.contactPhone} onChange={e => set('contactPhone', e.target.value)} /></Field>
        <Field label="Timezone">
          <select className="admin-form-control" value={settings.timezone} onChange={e => set('timezone', e.target.value)}>
            <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
            <option value="UTC">UTC</option>
            <option value="America/New_York">America/New_York (EST)</option>
          </select>
        </Field>
        <Field label="Currency">
          <select className="admin-form-control" value={settings.currency} onChange={e => set('currency', e.target.value)}>
            <option value="INR">INR ($)</option>
            <option value="USD">USD ($)</option>
          </select>
        </Field>
        <Field label="Language">
          <select className="admin-form-control" value={settings.language} onChange={e => set('language', e.target.value)}>
            <option value="en">English</option>
            <option value="hi">Hindi</option>
          </select>
        </Field>
        <ToggleRow label="Maintenance Mode" desc="Temporarily disable platform access for users" value={settings.maintenanceMode} onChange={v => set('maintenanceMode', v)} />
        <ToggleRow label="Open Registrations" desc="Allow new users and restaurants to register" value={settings.registrationsOpen} onChange={v => set('registrationsOpen', v)} />
      </Section>

      <Section icon={SettingsIcon} title="Business Rules">
        <Field label="Minimum Order Amount ($)"><input type="number" className="admin-form-control" value={settings.minOrderAmount} onChange={e => set('minOrderAmount', +e.target.value)} /></Field>
        <Field label="Max Delivery Radius (km)"><input type="number" className="admin-form-control" value={settings.maxDeliveryRadius} onChange={e => set('maxDeliveryRadius', +e.target.value)} /></Field>
        <Field label="Default Commission Rate (%)"><input type="number" className="admin-form-control" value={settings.defaultCommissionRate} onChange={e => set('defaultCommissionRate', +e.target.value)} /></Field>
        <Field label="GST Rate (%)"><input type="number" className="admin-form-control" value={settings.gstPercent} onChange={e => set('gstPercent', +e.target.value)} /></Field>
      </Section>

      <Section icon={Bell} title="Notifications">
        <ToggleRow label="Email Notifications" desc="Send order/system alerts via email" value={settings.emailNotifications} onChange={v => set('emailNotifications', v)} />
        <ToggleRow label="SMS Notifications" desc="Send SMS for critical events" value={settings.smsNotifications} onChange={v => set('smsNotifications', v)} />
        <ToggleRow label="Push Notifications" desc="Send push notifications to mobile apps" value={settings.pushNotifications} onChange={v => set('pushNotifications', v)} />
        <ToggleRow label="Order Alerts" desc="Alert admins on large/suspicious orders" value={settings.orderAlerts} onChange={v => set('orderAlerts', v)} />
        <ToggleRow label="Payment Alerts" desc="Alert admins on failed/disputed payments" value={settings.paymentAlerts} onChange={v => set('paymentAlerts', v)} />
      </Section>

      <Section icon={Shield} title="Security">
        <ToggleRow label="Two-Factor Authentication" desc="Require 2FA for all admin logins" value={settings.twoFactorAdmin} onChange={v => set('twoFactorAdmin', v)} />
        <Field label="IP Whitelist (comma-separated)">
          <input className="admin-form-control" placeholder="e.g. 192.168.1.1, 10.0.0.0/24" value={settings.ipWhitelist} onChange={e => set('ipWhitelist', e.target.value)} />
        </Field>
      </Section>

      <Section icon={Database} title="Financial Automation">
        <ToggleRow label="Auto Settlement" desc="Automatically process settlements on schedule" value={settings.autoSettlement} onChange={v => set('autoSettlement', v)} />
        <ToggleRow label="Auto-Approve Refunds" desc="Auto-approve refunds below limit" value={settings.refundAutoApprove} onChange={v => set('refundAutoApprove', v)} />
        <Field label="Settlement Frequency">
          <select className="admin-form-control" value={settings.settlementFrequency} onChange={e => set('settlementFrequency', e.target.value)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </Field>
        <Field label="Auto-Approve Refund Limit ($)">
          <input type="number" className="admin-form-control" value={settings.refundLimit} onChange={e => set('refundLimit', +e.target.value)} />
        </Field>
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 32 }}>
        <Button variant="primary" icon={<Save size={14} />} onClick={save}>Save All Changes</Button>
      </div>
    </div>
  );
};

export default Settings;
